// assets/services.product-form.js

class ProductFormArea {
  constructor(root) {
    this.root = root;
    this.formInitialized = false;
    this.selectedVariants = new Set();
    this.currentVariant = null;

    if (!this.root) {
      console.warn("ProductFormArea: missing required elements");
      return;
    }

    this.init();
  }

  /* =========
     Helpers
     ========= */

  formatMoney(cents) {
    const value = Number(cents) || 0;

    // Shopify formatter (padrão)
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(value, window.Shopify.money_format);
    }

    // Fallback BR
    return (value / 100).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  getCurrentQuantity() {
    const qtyInput = this.root.querySelector('input[name="quantity"]');
    const qty = parseInt(qtyInput?.value, 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  // Tenta extrair preço (em centavos) de variantes que podem vir em formatos diferentes
  getVariantPriceCents(variant) {
    if (!variant) return 0;

    // Shopify variants geralmente têm .price em centavos
    if (variant.price != null && variant.price !== "") {
      const p = Number(variant.price);
      if (Number.isFinite(p)) return p;
    }

    // Alguns payloads podem vir com priceV2.amount (string em reais)
    const amount =
      variant.priceV2?.amount ??
      variant.price?.amount ??
      variant.price_amount ??
      variant.amount;

    if (amount != null && amount !== "") {
      const n = Number(amount);
      if (Number.isFinite(n)) return Math.round(n * 100);
    }

    return 0;
  }

  updateBuyButtonTotal() {
    const priceEl = this.root.querySelector("product-buy-price[data-price]");
    if (!priceEl) return;

    // Preço unitário do produto principal (já com desconto, se aplicável) — em centavos
    const mainUnitDiscounted = Number(priceEl.dataset.price);
    if (!Number.isFinite(mainUnitDiscounted)) return;

    const qty = this.getCurrentQuantity();

    // Ratio de desconto baseado no preço "raw" do current variant
    const mainUnitRaw = this.getVariantPriceCents(this.currentVariant);
    let discountRatio = 1;

    if (Number.isFinite(mainUnitRaw) && mainUnitRaw > 0) {
      discountRatio = mainUnitDiscounted / mainUnitRaw;
    }

    // Soma upsells selecionados (por unidade)
    let upsellsUnitRaw = 0;

    if (this.selectedVariants && this.selectedVariants.size) {
      for (const v of this.selectedVariants) {
        if (!v) continue;
        if (v.current_product) continue;

        upsellsUnitRaw += this.getVariantPriceCents(v);
      }
    }

    // Aplica o mesmo ratio do produto principal nos upsells (mantém consistência B2B)
    const upsellsUnitDiscounted = Math.round(upsellsUnitRaw * discountRatio);

    // Total por unidade
    const perUnitTotal = mainUnitDiscounted + upsellsUnitDiscounted;

    // Total final
    const total = perUnitTotal * qty;

    // Atualiza o texto exibido no botão
    priceEl.textContent = this.formatMoney(total);
  }

  /* =========
     Init / Events
     ========= */

  initFormEvents() {
    if (this.formInitialized) return;

    const attachListeners = () => {
      if (this.formInitialized) return;

      // 1) Upsell / seleção de variantes extras
      this.root.addEventListener("variant-upsell:product-checked", (e) => {
        const product = e.detail?.product;
        const variant = e.detail?.variant;

        if (variant) {
          this.onVariantSelected(variant, product);
        }
      });

      // 2) Atualiza preço do botão ao mudar quantidade
      this.root.addEventListener("input", (e) => {
        if (e.target && e.target.matches('input[name="quantity"]')) {
          this.updateBuyButtonTotal();
        }
      });

      this.root.addEventListener("change", (e) => {
        if (e.target && e.target.matches('input[name="quantity"]')) {
          this.updateBuyButtonTotal();
        }
      });

      // 3) Atualiza quando variantes/upsells mudam (pra refletir novo data-price)
      this.root.addEventListener("product-form:variants-updated", () => {
        setTimeout(() => this.updateBuyButtonTotal(), 0);
      });

      // 4) Atualiza na carga inicial
      this.updateBuyButtonTotal();

      this.formInitialized = true;
    };

    // Alguns temas re-renderizam trechos do form; observer garante que os listeners entram
    const observer = new MutationObserver(() => attachListeners());
    observer.observe(this.root, { childList: true, subtree: true });

    // fallback caso nada seja mutado
    setTimeout(() => attachListeners(), 2000);
  }

  init() {
    this.initFormEvents();

    const meta = this.root.querySelector('meta[name="current-variant"]');
    if (meta?.content) {
      try {
        const parsed = JSON.parse(meta.content);
        this.currentVariant = parsed;

        this.selectedVariants.add({
          ...parsed,
          current_product: true,
        });
      } catch (err) {
        console.warn("ProductFormArea: failed to parse current-variant meta", err);
      }
    }

    this.updateBuyButtonTotal();
  }

  onVariantSelected(variant, product) {
    const productType = product?.type;
    const variantID = String(variant?.id);

    if (!variantID) return;

    const variantIDInput = this.root.querySelector('input[name="id"]');
    if (!variantIDInput) return;

    if (!this.selectedVariants) {
      this.selectedVariants = new Set();
    }

    // Check if this variant is already in the Set
    const existing = Array.from(this.selectedVariants).find(
      (v) => v && String(v.id) === variantID
    );

    if (existing) {
      // Remove by finding the object and deleting it
      this.selectedVariants.delete(existing);

      if (productType && productType.toLowerCase().includes("customization")) {
        document.dispatchEvent(
          new CustomEvent("product-form:customization-drawer:hide", {
            bubbles: true,
          })
        );
      }
    } else {
      // Add the new variant object
      this.selectedVariants.add(variant);

      if (productType && productType.toLowerCase().includes("customization")) {
        document.dispatchEvent(
          new CustomEvent("product-form:customization-drawer:show", {
            bubbles: true,
          })
        );
      }
    }

    // Sync the hidden input
    // (mantive seu comportamento atual)
    variantIDInput.value = Array.from(this.selectedVariants)
      .filter(Boolean)
      .map((v) => String(v.id))
      .join(",");

    this.root.dispatchEvent(
      new CustomEvent("product-form:variants-updated", {
        bubbles: true,
        detail: {
          variants: Array.from(this.selectedVariants).filter(Boolean),
        },
      })
    );

    // Força atualização do total no botão após update de variantes
    setTimeout(() => this.updateBuyButtonTotal(), 0);
  }
}

// Inicializa todos os forms de produto
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll('form[is="product-form"]').forEach((el) => {
    new ProductFormArea(el);
  });
});
